import React from "react";
//#region types
import { NestedTableProps, NestedTableRowCommonKeys } from "models/propTypes/simpleTableProps";
import { TableEnums } from "models/enums";
import { CommonConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import LoadingWithLogo from "../../loading/loadingWithLogo";
import CheckBox from "../../forms/baseFields/checkBox";
import NestedTableRow from "./nestedTableRow";
import TableFooter from "../tableFooter";
import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import TableSkeleton from "../../skeletons/tableSkeleton";
import useTableHelper from "../useTableHelper";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const NestedTable = <RowType extends Record<string, any>, T extends Record<string, any>>(
  props: React.PropsWithChildren<NestedTableProps<RowType, T>>
) => {
  const {
    properties,
    repeatProperties,
    childrenKey,
    hasMultiLevels,
    allowCheckBoxSelection,
    onRowSelectionsChange,
    onRowsUpdate,
    hasChildren,
    rowDisplayType,
    maxRowCount,
    pagination = false,
    hidePaginationIfSinglePage,
    loading,
    checkBoxesCheckedAsDefault,
    initialExpansionRowIndex,
    rowFooterRenderer,
  } = props;

  let tableRef: HTMLDivElement | null = null;

  const [results, setResults] = React.useState<{
    rows: (RowType & NestedTableRowCommonKeys)[];
    repeatColumnLength: number;
  }>({ rows: [], repeatColumnLength: 0 });
  const [scrolling, setScrolling] = React.useState<boolean>(false);
  const [selectedAll, setSelectedAll] = React.useState<boolean>(false);
  const [selectAllDisabled, setSelectAllDisabled] = React.useState<boolean>(false);
  const [page, setPage] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(maxRowCount ?? CommonConstants.DefaultTableRowCount);

  const { getNestedTableColumnTitle } = useTableHelper();

  React.useEffect(() => {
    const rows = onRowsUpdate ? onRowsUpdate(results.rows, props.rows) : props.rows;
    const nonTitleRows = rowDisplayType
      ? props.rows.filter((r) => rowDisplayType(r) !== TableEnums.RowDisplayType.Title)
      : rows;

    const currentPage = page + 1;
    const items = (
      pagination ? rows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage) : rows
    ) as RowType[];
    setResults({
      rows: items,
      repeatColumnLength: repeatProperties && nonTitleRows.length ? nonTitleRows[0][repeatProperties.key]?.length : 0,
    });
    setTotal(rows.length);
  }, [props.rows, page, rowsPerPage]);

  React.useEffect(() => {
    return () => {
      tableRef = null;
    };
  }, []);

  React.useEffect(() => {
    toggleInitialRowExpansion();
  }, [initialExpansionRowIndex, props.rows, repeatProperties, childrenKey]);

  React.useEffect(() => {
    if (loading && props.rows === undefined) return;

    if (checkBoxesCheckedAsDefault) {
      setSelectedAll(true);

      const rows = props.rows as (RowType & NestedTableRowCommonKeys)[];
      rows.forEach((r) => (r.selected = true));
      setResults({ ...results, rows: rows });

      onRowSelectionsChange && onRowSelectionsChange(rows.filter((r) => r.selected));
    } else {
      setSelectedAll(allEnabledRowsSelected(props.rows));
    }
    setSelectAllDisabled(props.rows?.every((r) => r.disabled));
  }, [loading]);

  const allEnabledRowsSelected = (rows: RowType[]): boolean => {
    const allEnabledRows = rows?.filter((r) => !r.disabled);
    return allEnabledRows.some((r) => r) && allEnabledRows.every((r) => r.selected);
  };

  const onTableScroll = () => {
    if (tableRef)
      if (tableRef.scrollLeft > 0 && !scrolling) {
        setScrolling(true);
      } else if (tableRef.scrollLeft === 0 && scrolling) {
        setScrolling(false);
      }
  };

  const toggleInitialRowExpansion = () => {
    const rows = props.rows as (RowType & NestedTableRowCommonKeys)[];

    const nonTitleRows = rowDisplayType
      ? props.rows.filter((r) => rowDisplayType(r) !== TableEnums.RowDisplayType.Title)
      : rows;

    if (rows && rows.length > 0) {
      expandList(rows, false);

      if (initialExpansionRowIndex !== undefined && initialExpansionRowIndex >= 0) {
        rows[initialExpansionRowIndex].expanded = true;

        const key = typeof childrenKey === "function" ? "children" : (childrenKey as string);
        const children = rows[initialExpansionRowIndex][key];
        if (children) {
          expandList(children, true);
        }
      }

      setResults({
        rows: rows,
        repeatColumnLength: repeatProperties && nonTitleRows.length ? nonTitleRows[0][repeatProperties.key]?.length : 0,
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- will be implemented when design available https://app.clickup.com/t/4523827/DEV-8498
  const toggleExpansionForAll = (expand: boolean) => {
    const updatedRows = [...results.rows];

    expandList(updatedRows, expand);
    setResults({ ...results, rows: updatedRows });
  };

  const expandList = (items: any[], expand: boolean) => {
    const key = typeof childrenKey === "function" ? "children" : (childrenKey as string);

    items.forEach((item) => {
      item.expanded = expand;

      const children = item[key];
      if (children) {
        expandList(children, expand);
      }
    });
  };

  const toggleExpansion = async (rowIndex: number, parentIndex: number, path: number[]) => {
    const updatedRows = [...results.rows];
    const key = typeof childrenKey === "function" ? "children" : (childrenKey as string);
    if (path.length === 0) {
      if (!updatedRows[rowIndex].expanded && typeof childrenKey === "function" && !updatedRows[rowIndex][key]?.length)
        await loadChildren(key, updatedRows[rowIndex]);
      updatedRows[rowIndex].expanded = !updatedRows[rowIndex].expanded;
    } else {
      let item: any = updatedRows[path[0]];
      path.forEach((pathIndex, index) => {
        if (index === 0) return;
        item = item[key][pathIndex];
      });

      item = item[key][rowIndex];

      if (typeof childrenKey === "function" && !item.expanded && !item.children?.length) await loadChildren(key, item);

      item.expanded = !item.expanded;
    }
    setResults({ ...results, rows: updatedRows });
  };

  const toggleSelection = (rowIndex: number) => {
    const updatedRows = [...results.rows];
    const newToggleValue = !updatedRows[rowIndex].selected;

    updatedRows[rowIndex].selected = !updatedRows[rowIndex].selected;
    setResults({ ...results, rows: updatedRows });
    if (!newToggleValue) {
      setSelectedAll(false);
    } else {
      if (allEnabledRowsSelected(updatedRows)) {
        setSelectedAll(true);
      }
    }

    onRowSelectionsChange && onRowSelectionsChange(updatedRows.filter((r) => r.selected));
  };

  const toggleSelectAll = () => {
    const allSelected = !selectedAll;
    const updatedResultRows = [...results.rows];

    updatedResultRows.forEach((r) => {
      if (!r.disabled) r.selected = allSelected;
    });

    let updatedRows = [...updatedResultRows];

    if (pagination) {
      updatedRows = [...props.rows];
      updatedRows.forEach((r) => {
        if (!r.disabled) r.selected = allSelected;
      });
    }

    setSelectedAll(allSelected);
    setResults({ ...results, rows: updatedResultRows });

    onRowSelectionsChange && onRowSelectionsChange(allSelected ? updatedRows : []);
  };

  const loadChildren = async (childrenFieldName: string, parent: any) => {
    if (typeof childrenKey === "function") {
      const children = await childrenKey(parent);
      parent[childrenFieldName] = children;
      return parent;
    }
  };

  const columns = Object.entries(properties).filter(([key, value]) => key !== "group");

  const repeatPropertiesLength = repeatProperties?.properties ? Object.entries(repeatProperties.properties).length : 0;

  const repeatPropertiesList =
    repeatProperties &&
    Object.entries(repeatProperties.properties).filter(
      ([repeatKey, value]) => !props.hiddenColumns?.some((hc) => hc === repeatKey)
    );

  const onPageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const onRowsPerPageChange = (rowsPerPage: number) => {
    setPage(0);
    setRowsPerPage(rowsPerPage);
  };

  const tableComponent = React.useMemo(
    () => (
      <Grid
        item
        container
        className={clsx(
          cssExports.tableContainerOuter,
          cssExports.nestedTableContainer,
          scrolling ? cssExports.scrolling : "",
          props.className,
          allowCheckBoxSelection ? cssExports.nestedTableWithCheckBoxes : ""
        )}
      >
        {loading && results.rows.length ? <LoadingWithLogo className={cssExports.loadingList} /> : <></>}
        {/* Until we receive a better design https://app.clickup.com/t/4523827/DEV-8498
         <Grid container direction="row" className={cssExports.tableHeaderActions}>
          <ActionLinkButton onClick={toggleExpansionForAll.bind(null, true)}>
            <Icon icon={IconDefinitionConstants.WTableExpand} />
          </ActionLinkButton>
          <Grid item>
            <Divider className={cssExports.dividerGray} orientation="vertical" />
          </Grid>
          <ActionLinkButton onClick={toggleExpansionForAll.bind(null, false)}>
            <Icon icon={IconDefinitionConstants.WTableCollapse} />
          </ActionLinkButton>
        </Grid> */}

        {loading && !results.rows.length ? (
          <TableSkeleton />
        ) : (
          <TableContainer
            ref={(instance: HTMLDivElement | null) => {
              tableRef = instance;
            }}
            onScroll={onTableScroll}
          >
            <Table>
              <TableHead className={clsx(cssExports.tableHead)}>
                {repeatProperties?.filterField ? (
                  <TableRow>
                    <TableCell
                      className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.first)}
                    ></TableCell>
                    <TableCell
                      key={`tab-headerTop`}
                      className={clsx(cssExports.tableCell, cssExports.freezeCell, cssExports.second)}
                      colSpan={columns.length + 2}
                    ></TableCell>
                    <>
                      {[...Array(results.repeatColumnLength)].map((col, repeatIndex) => (
                        <TableCell
                          key={`tab-repeat-headerTop-${repeatIndex}`}
                          className={clsx(cssExports.tableCell, cssExports.repeatCellHeader)}
                          colSpan={Object.entries(repeatProperties.properties).length}
                        >
                          {repeatProperties.filterField && repeatProperties.filterField(repeatIndex, loading)}
                        </TableCell>
                      ))}
                      {repeatProperties?.addRepeatColumnField && (
                        <TableCell
                          key={`tab-repeat-headerTop-new-${results.repeatColumnLength}`}
                          className={clsx(cssExports.tableCell, cssExports.addRepeatCell)}
                        >
                          {repeatProperties.addRepeatColumnField &&
                            repeatProperties.addRepeatColumnField(results.repeatColumnLength)}
                        </TableCell>
                      )}
                    </>
                  </TableRow>
                ) : (
                  <></>
                )}
                <TableRow>
                  <TableCell
                    className={clsx(
                      cssExports.tableCell,
                      cssExports.emptyCell,
                      cssExports.first,
                      allowCheckBoxSelection ? cssExports.select : ""
                    )}
                  >
                    {allowCheckBoxSelection ? (
                      <CheckBox checked={selectedAll} onChange={toggleSelectAll} disabled={selectAllDisabled} />
                    ) : (
                      <></>
                    )}
                  </TableCell>
                  {columns.map(([headerKey, value], index) => (
                    <TableCell
                      key={`tab-header-${index}`}
                      className={clsx(
                        cssExports.tableCell,
                        index === columns.length - 1 && repeatProperties ? cssExports.freezeCell : "",
                        index === 0 ? cssExports.second : "",
                        value?.className
                      )}
                      align={value?.alignment}
                      style={{
                        width: value?.width,
                      }}
                      colSpan={index === 0 ? 3 : undefined}
                    >
                      {value?.description && getNestedTableColumnTitle(value.description)}
                    </TableCell>
                  ))}
                  {repeatPropertiesList ? (
                    [...Array(results.repeatColumnLength)].map((col, repeatIndex) => (
                      <React.Fragment key={`repeat-columns-${repeatIndex}`}>
                        {repeatPropertiesList.map(([repeatKey, value], cellIndex) => (
                          <TableCell
                            key={`tab-repeat-header-${repeatIndex}-${cellIndex}`}
                            className={clsx(
                              cssExports.tableCell,
                              cellIndex === repeatPropertiesLength - 1 && repeatProperties?.showTotal
                                ? cssExports.repeatEnd
                                : ""
                            )}
                            align={value?.alignment}
                            style={{
                              width: value?.width,
                            }}
                          >
                            {value?.description && getNestedTableColumnTitle(value.description, repeatIndex)}
                          </TableCell>
                        ))}
                      </React.Fragment>
                    ))
                  ) : (
                    <></>
                  )}
                  {repeatProperties?.showTotal ? (
                    <TableCell
                      key={`tab-repeat-header-total`}
                      className={clsx(cssExports.tableCell, cssExports.totalCell)}
                      align="right"
                    >
                      {`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InTotal)}`}
                    </TableCell>
                  ) : (
                    <></>
                  )}
                  <TableCell
                    key={`tab-repeat-headerTop-new-${results.repeatColumnLength}`}
                    className={clsx(cssExports.tableCell, cssExports.addRepeatCell)}
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={cssExports.tableBody}>
                {results.rows.length > 0 &&
                  results.rows.map((row, index, array) => (
                    <React.Fragment key={`rows-${index}`}>
                      {properties.group &&
                        (index === 0 ||
                          (properties.group.formatter
                            ? properties.group.formatter(row, index) !==
                              properties.group.formatter(array[index - 1], index - 1)
                            : row["group"] !== array[index - 1]["group"])) && (
                          <TableRow className={cssExports.tableSubHead}>
                            <TableCell
                              className={clsx(cssExports.tableCell, cssExports.content)}
                              colSpan={3 + (props.allowCheckBoxSelection ? 1 : 0)}
                            >
                              {properties.group.formatter ? properties.group.formatter(row, index) : row["group"]}
                            </TableCell>
                            <TableCell
                              colSpan={
                                columns.length + results.repeatColumnLength * (repeatPropertiesList?.length ?? 0)
                              }
                              className={cssExports.tableCell}
                            ></TableCell>
                          </TableRow>
                        )}
                      <NestedTableRow
                        key={`tab-row-${0}-${index}`}
                        row={row}
                        index={index}
                        parentIndex={-1}
                        path={[]}
                        depth={0}
                        childrenKey={typeof childrenKey === "function" ? "children" : (childrenKey as string)}
                        hasChildren={hasChildren}
                        columns={columns}
                        repeatProperties={repeatProperties}
                        repeatColumnLength={results.repeatColumnLength}
                        hasMultiLevels={hasMultiLevels}
                        allowCheckBoxSelection={allowCheckBoxSelection}
                        rowDisplayType={props.rowDisplayType}
                        rowIcon={props.rowIcon}
                        rowClickHandler={props.rowClickHandler}
                        toggleExpansionHandler={toggleExpansion}
                        toggleSelectionHandler={toggleSelection}
                        hiddenColumns={props.hiddenColumns?.map((hd) => hd.toString())}
                        checkBoxesCheckedAsDefault={checkBoxesCheckedAsDefault}
                        rowFooterRenderer={rowFooterRenderer}
                      />
                    </React.Fragment>
                  ))}
                {pagination && (
                  <TableRow>
                    <TableCell
                      className={cssExports.tableFooterCell}
                      colSpan={3 + columns.length + results.repeatColumnLength * (repeatPropertiesList?.length ?? 0)}
                    >
                      <TableFooter
                        total={total}
                        rowsPerPage={rowsPerPage}
                        currentPage={page}
                        advancedPagination={pagination === "advanced"}
                        changePageHandler={onPageChange}
                        changeRowsPerPageHandler={onRowsPerPageChange}
                        hideIfSinglePage={hidePaginationIfSinglePage}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    ),
    [results, scrolling, selectedAll, loading]
  );

  return tableComponent;
};

export default NestedTable;
